"use client"

import { Toaster as Sonner, type ToasterProps } from "sonner"
import { CircleCheck, CircleAlert } from "lucide-react"
import { cn } from "./cn"

/**
 * aSTRING is dark-only (no theme switcher — see docs/adr on i18n/theming),
 * so this skips next-themes entirely and always renders Sonner's dark
 * palette, styled with our own tokens instead of Sonner's defaults.
 */
const Toaster = ({ className, toastOptions, ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="dark"
      position="top-center"
      duration={4000}
      closeButton
      className={cn("toaster group safe-area-top", className)}
      icons={{
        success: <CircleCheck className="size-[18px] text-accent-text" strokeWidth={2} />,
        error: <CircleAlert className="size-[18px] text-danger-text" strokeWidth={2} />,
      }}
      toastOptions={{
        ...toastOptions,
        classNames: {
          toast: cn(
            "group toast flex items-start gap-2.5 rounded-lg border border-hairline bg-bg-2 px-4 py-3.5",
            "text-body text-text-1",
            "shadow-[0_12px_28px_-6px_var(--color-scrim)]",
            "data-[type=success]:border-accent/25",
            "data-[type=error]:border-danger/35",
            toastOptions?.classNames?.toast
          ),
          title: cn(
            "text-body font-medium leading-[1.3] text-text-1",
            toastOptions?.classNames?.title
          ),
          description: cn(
            "mt-0.5 text-body leading-[1.45] text-text-2",
            toastOptions?.classNames?.description
          ),
          actionButton: cn(
            "!bg-accent !text-on-accent rounded-sm !px-2.5 !py-1.5 text-body font-medium hover:!opacity-90",
            toastOptions?.classNames?.actionButton
          ),
          cancelButton: cn(
            "!bg-surface-strong !text-text-2 rounded-sm !px-2.5 !py-1.5 text-body hover:!text-text-1",
            toastOptions?.classNames?.cancelButton
          ),
          closeButton: cn(
            "!bg-surface-strong !border-hairline !text-text-3 hover:!text-text-1",
            toastOptions?.classNames?.closeButton
          ),
          icon: cn("mt-0.5 shrink-0", toastOptions?.classNames?.icon),
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
